import React, { useState, useEffect } from 'react';
import { ErrorHandler } from './ErrorHandler';
import { USER_REQUEST_URL, postFetchRequest } from '../store/requests';
import { ACTIVE_MODAL_ITEM_EMAIL, ACTIVE_MODAL_ITEM_PASSWORD, ERROR_TYPE_PASSWORD, LOCALE_EN, LOCALE_RU, TABLET_WIDTH } from '../constant';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { MODAL_SMALL_SIZE, MODAL_SUPER_SMALL_SIZE } from './helper';
import PasswordField from './../shared/PasswordField';
import LocaleSwitcher from './../shared/LocaleSwitcher';
import { Button, Input, Select } from '@chapche/ui';
import useWindowDimensions from '../hooks/useWindowDimension';
import { NEW_PASSWORD_KEY, OLD_PASSWORD_KEY, REPEAT_PASSWORD_KEY, loginManager } from '../shared/loginManager';
import {errorCallback, makeUrl, successCallbackEmpty} from "../util";
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS } from '../constant/timezones';
import { setCurrentUser } from '../services/currentUserStore';
import { useAuth } from '../auth/AuthContext';

const LOCALE_OPTIONS = [
    { value: LOCALE_RU, label: 'Русский' },
    { value: LOCALE_EN, label: 'English' },
];

const TIMEZONE_SELECT_OPTIONS = TIMEZONE_OPTIONS.map((it) => ({
    value: it.code,
    label: it.name,
}));

const ChangeModalContent = ({handleChange, handleClose, activeModalItem, saveChanges, error }) => {
    const [isDisabled, setIsDisabled] = useState(true);
    const { i18n } = useTranslation();

    const changeIsDisabled = () => {
        setIsDisabled(!loginManager.canSendRequest());
    };

    useEffect(() => {
        loginManager.registerLoginUpdateObserver(changeIsDisabled);
        return () => loginManager.unRegisterLoginUpdateObserver(changeIsDisabled);
    }, []);
    return (
        <>
            <h2 className="modal__title block__title-h3">{i18n.t(`app.page.profile.modal.${activeModalItem.name}.title`)}</h2>
            <p className="block__content modal__text">{i18n.t(`app.page.profile.modal.${activeModalItem.name}.content`)}</p>
            {activeModalItem.fields.map((it, index) => (
                <React.Fragment key={it.label + it.type}>
                    {it.type !== 'email' ? 
                        <PasswordField label={it.label} passwordType={it.type} />
                        :
                        <label key={it.name} className="block__content profile__label profile__label-modal">
                            {it.name}
                            <Input
                                fullWidth
                                className="mt-0"
                                type="email"
                                value={it.value}
                                name={it.name}
                                onChange={(e) => handleChange(e, index)}
                            />
                        </label>}
                    {index === 2 && error.type === ERROR_TYPE_PASSWORD ? <p className="block__bullet alert profile__alert">{error.message}</p> : ''}
                </React.Fragment>
            ))}
            <div className="modal__button-group">
                <Button variant="outline" onClick={() => handleClose(null)}>
                    {i18n.t(`app.page.profile.modal.${activeModalItem.name}.cancel`)}
                </Button>
                <Button disabled={isDisabled} onClick={saveChanges}>
                    {i18n.t(`app.page.profile.modal.${activeModalItem.name}.change`)}
                </Button>
            </div>
        </>
    );
};

const ProfilePage = () => {
    const { user: currentUserData } = useAuth();
    const [profileData, setProfileData] = useState({
        ...currentUserData,
        timezone: currentUserData?.timezone || DEFAULT_TIMEZONE,
    });
    const [activeModalItem, setActiveModalItem] = useState(null);
    const [error, setError] = useState({type:'', message: ''});
    const { width } = useWindowDimensions();
    const { i18n } = useTranslation();

    useEffect(() => {
        if (currentUserData?.id) {
            const next = {
                ...currentUserData,
                timezone: currentUserData.timezone || DEFAULT_TIMEZONE,
            };
            setProfileData(next);
            setCurrentUser(next);
        }
    }, [currentUserData?.id, currentUserData?.timezone]);

    const handleChangeProfileData = (e) => {
        profileData[e.target.name] = e.target.value;
        setProfileData({...profileData});
    };

    const handleChangeUser = (e) => {
        profileData[e.target.name] = e.target.value;
        setProfileData({...profileData});
        const data = {
            name: profileData.name,
            appRoleId: profileData.appRole.id,
        };
        postFetchRequest(makeUrl([USER_REQUEST_URL, profileData.id, '/']), data, () => {}, errorCallback, true);
    };

    const handleChangeProfile = (e, index) => {
        setError({type:'', message: ''});
        activeModalItem.fields[index].value = e.target.value;
        setActiveModalItem({...activeModalItem});
    };

    const successUpdateEmailCallback = (response) => {
        if(response.ok) {
            setActiveModalItem(null);
            profileData.isChangedEmail = true;
            profileData.email = activeModalItem.fields[0].value;
            setProfileData({...profileData});
        }
    };

    const successUpdatePasswordCallback = (response) => {
        if(response.ok) {
            setActiveModalItem(null);
        }
    };


    const saveChanges = () => {
        if(activeModalItem.name === ACTIVE_MODAL_ITEM_EMAIL) {
            const data = {
                email: activeModalItem.fields[0].value,
            };
            postFetchRequest(makeUrl([USER_REQUEST_URL, profileData.id , `/${ACTIVE_MODAL_ITEM_EMAIL}/`]), data, successUpdateEmailCallback, errorCallback, true);
        } else {
            loginManager.sendChangePasswordRequest(profileData.id, successUpdatePasswordCallback);
        }
    };

    const handleUpdateLocale = (newLocale) => {
        const newProfileData = {...profileData, locale: newLocale };
        i18n.changeLanguage(newLocale)
        setProfileData(newProfileData);
        postFetchRequest(
            makeUrl([USER_REQUEST_URL, profileData.id, '/locale/']),
            {locale: newLocale},
            successCallbackEmpty,
            errorCallback,
            true);
        
    };

    const handleUpdateTimezone = (option) => {
        const timezone = typeof option === 'string' ? option : (option?.code || option?.id);
        if (!timezone || timezone === profileData.timezone) {
            return;
        }
        const nextProfile = { ...profileData, timezone };
        setProfileData(nextProfile);
        setCurrentUser(nextProfile);
        postFetchRequest(
            makeUrl([USER_REQUEST_URL, profileData.id, '/timezone/']),
            { timezone },
            successCallbackEmpty,
            errorCallback,
            true,
        );
    };

    const handleCreateEditPasswordObject = () => {
        return {
            name: ACTIVE_MODAL_ITEM_PASSWORD,
            fields: [
                {
                    label: i18n.t('app.page.profile.modal.password.currentPassword'),
                    type: OLD_PASSWORD_KEY,
                },
                {
                    label: i18n.t('app.page.profile.modal.password.newPassword'),
                    type: NEW_PASSWORD_KEY,
                },
                {
                    label: i18n.t('app.page.profile.modal.password.repeatNewPassword'),
                    type: REPEAT_PASSWORD_KEY,
                },
            ],
        };
    };

    const handleCreateEditEmailObject = () => {
        return {
            name: ACTIVE_MODAL_ITEM_EMAIL,
            fields: [
                {
                    name: i18n.t('app.page.profile.email'),
                    value: '',
                    type: 'email',
                },
            ],
        };
    };

    return (
        <section className="workspace workspace__profile">
            {activeModalItem ? 
                <Modal content={
                    <ChangeModalContent handleChange={handleChangeProfile} saveChanges={saveChanges} handleClose={setActiveModalItem} 
                        error={error} activeModalItem={activeModalItem} setActiveModalItem={setActiveModalItem}/>} 
                handleCloseModal={setActiveModalItem} size={activeModalItem.name === ACTIVE_MODAL_ITEM_EMAIL ? MODAL_SMALL_SIZE : MODAL_SUPER_SMALL_SIZE} /> 
                : 
                ''}
            <h1 className="profile__title block__title-h2">
                {i18n.t('app.page.profile.title')}
                {profileData.id && width > TABLET_WIDTH ?
                    <div className="locale__select locale__select-profile">
                        <LocaleSwitcher localeCode={profileData.locale} notifyListener={handleUpdateLocale}/>
                    </div>
                    :
                    null}
            </h1>
            <div className="profile__wrapper">
                <article className="profile__article">
                    <h2 className="profile__subtitle block__title-h3">{i18n.t('app.page.profile.myData')}</h2>
                    <form className="profile__form">
                        <label className="block__text profile__label">{i18n.t('app.page.profile.name')}
                            <Input
                                fullWidth
                                className="mt-0"
                                value={profileData.name ? profileData.name : ''}
                                name="name"
                                onChange={handleChangeProfileData}/>
                        </label>
                        <label className="block__text profile__label">{i18n.t('app.page.profile.email')}
                            <Input
                                fullWidth
                                className="mt-0"
                                state={profileData.isChangedEmail ? 'warning' : 'default'}
                                warningText={profileData.isChangedEmail ? i18n.t('app.page.profile.confirmEmail') : undefined}
                                value={profileData.email ? profileData.email : ''}
                                name="email"
                                onChange={handleChangeProfileData}
                                disabled/>
                        </label>
                        <p className="block__bullet profile__change" onClick={() => setActiveModalItem(handleCreateEditEmailObject())}>{i18n.t('app.page.profile.changeEmail')}</p>
                        <p className="block__bullet profile__change" onClick={() => setActiveModalItem(handleCreateEditPasswordObject())}>{i18n.t('app.page.profile.changePassword')}</p>
                        {width <= TABLET_WIDTH ? 
                            <Select
                                fullWidth
                                className="mt-0"
                                options={LOCALE_OPTIONS}
                                value={profileData.locale}
                                onChange={handleUpdateLocale}
                            />
                            :
                            null}
                        <label className="block__text profile__label">{i18n.t('app.page.profile.timezone')}
                            <Select
                                fullWidth
                                className="mt-0"
                                options={TIMEZONE_SELECT_OPTIONS}
                                value={profileData.timezone || DEFAULT_TIMEZONE}
                                onChange={handleUpdateTimezone}
                            />
                        </label>
                        <Button type="button" className="profile__button" onClick={handleChangeUser}>
                            {i18n.t('app.page.profile.change')}
                        </Button>
                    </form>
                </article>
                <article className="profile__article profile__article-history">
                    <h2 className="profile__subtitle block__title-h3">{i18n.t('app.page.profile.history')}</h2>
                    <ErrorHandler titleValue={i18n.t('app.page.profile.emptyData')}/>
                </article>
            </div>
        </section>
    );
};

export default ProfilePage;
